import { ColumnDefinition, FormState, SlotKey, SlotState } from './formTypes';
import { MATCH_EXISTING_ENABLED, SLOT_KEYS } from './constants';
import { isJsonArrayText } from './jsonRules';

export const slotStates = (
    form: FormState,
    matchExistingEnabled = MATCH_EXISTING_ENABLED,
): Record<SlotKey, SlotState> => {
    if (form.runMode !== 'new') {
        return SLOT_KEYS.reduce((acc, k) => {
            acc[k] = 'disabled';
            return acc;
        }, {} as Record<SlotKey, SlotState>);
    }

    const { catalogMode, extractObservations, extractSolutions, extractDiagnostics } = form;

    // Both catalog slots exist only to feed match-existing. While that mode is
    // switched off (or a fresh catalog is being built) they are not rendered at all.
    const catalogSlotState = (extract: boolean): SlotState =>
        !matchExistingEnabled || catalogMode !== 'match_existing'
            ? 'hidden'
            : extract
                ? 'required'
                : 'optional';

    return {
        historical_cases: 'required',
        // Each codes file lives under its own Advanced toggle: turning the toggle on
        // is a claim that the file exists, so it is required rather than optional.
        error_codes: form.useExactMatchErrorCodeClustering ? 'required' : 'hidden',
        solution_codes_filename: form.useExactMatchSolutionCodeClustering ? 'required' : 'hidden',
        observation_catalog: catalogSlotState(extractObservations),
        solutions_catalog: catalogSlotState(extractSolutions),
        part_info: 'optional',
        // Component-code reference list, gated by its own Advanced toggle like the two
        // exact-match slots above. The toggle is subordinate to extractDiagnostics:
        // component clustering is meaningless without diagnostic extraction, so the
        // slot stays hidden while that checkbox is off whatever the flag holds.
        diagnostic_component:
            extractDiagnostics && form.clusterDiagnosticsByComponents ? 'required' : 'hidden',
    };
};

export const isRowEmpty = (row: ColumnDefinition): boolean =>
    row.columns.length === 0 && row.definition.trim() === '' && row.relevantTo.length === 0;

export const isRowComplete = (row: ColumnDefinition): boolean =>
    row.columns.length > 0 && row.definition.trim() !== '' && row.relevantTo.length > 0;

export const columnDefinitionsValid = (form: FormState): boolean => {
    if (form.runMode !== 'new') return true;
    return form.columnDefinitions.every(r => isRowEmpty(r) || isRowComplete(r));
};

// Columns that must both live in a single uploaded file.
export const PART_REQUIRED_COLUMNS = ['part_id', 'part_description'] as const;
// Files eligible to carry the required part columns.
export const PART_COLUMN_FILES: SlotKey[] = ['historical_cases', 'part_info'];

const fileHasColumns = (columns: string[] | undefined, required: readonly string[]): boolean => {
    if (!columns) return false;
    const normalized = new Set(columns.map(c => c.trim().toLowerCase()));
    return required.every(rc => normalized.has(rc));
};

// At least one of the candidate files must contain both part_id and
// part_description (in the same file). Only enforced for new runs.
export const partColumnsValid = (form: FormState): boolean => {
    if (form.runMode !== 'new') return true;
    return PART_COLUMN_FILES.some(k => fileHasColumns(form.fileColumns[k], PART_REQUIRED_COLUMNS));
};

export const cleanColumnDefinitions = (
    form: FormState,
): { columns: string[]; definition: string; relevant_to: string[] }[] =>
    form.columnDefinitions.filter(isRowComplete).map(r => ({
        columns: r.columns,
        definition: r.definition.trim(),
        relevant_to: r.relevantTo,
    }));

// Only an ENABLED toggle's rules must be valid; a disabled toggle sends [] regardless.
export const jsonRulesValid = (form: FormState): boolean => {
    if (form.runMode !== 'new') return true;
    if (form.useSolutionCodeNonSemanticClustering && !isJsonArrayText(form.solutionCodeNonSemanticClusteringRules)) return false;
    if (form.useCustomPostProcessing && !isJsonArrayText(form.customPostProcessingRules)) return false;
    return true;
};

export const isSubmittable = (
    form: FormState,
    matchExistingEnabled = MATCH_EXISTING_ENABLED,
): boolean => {
    if (!form.tenantId) return false;
    if (!form.runMode) return false;

    if (form.runMode === 'resume') {
        return form.previousRun !== null && form.startStage !== null;
    }

    if (!form.catalogMode) return false;

    const states = slotStates(form, matchExistingEnabled);
    for (const k of SLOT_KEYS) {
        if (states[k] === 'required' && form.files[k] === null) return false;
    }
    if (!columnDefinitionsValid(form)) return false;
    if (!partColumnsValid(form)) return false;
    if (!jsonRulesValid(form)) return false;
    return true;
};

